import { AppIcon } from '@backrs_ui/common-ui';

import { personInitial, personName } from '@backrs_ui/grow-ui/src/utils/personName';

import { Avatar, Box, Chip, SxProps, Theme } from '@mui/material';

type CoachBadgeProps = Readonly<{
  id?: string;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  onClick?: () => void;
  onDelete?: () => void;
  /** Names the remove control for assistive tech; without it the icon reads as unlabelled. */
  deleteLabel?: string;
  deleteId?: string;
  disabled?: boolean;
}>;

export const CoachBadge = ({
  id,
  firstName,
  lastName,
  profileImageUrl,
  onClick,
  onDelete,
  deleteLabel,
  deleteId,
  disabled,
}: CoachBadgeProps) => {
  const person = { first_name: firstName, last_name: lastName };
  const name = personName(person);

  return (
    <Chip
      id={id}
      variant="badge"
      size="md"
      type="leading"
      disabled={disabled}
      clickable={!!onClick}
      onClick={onClick}
      icon={
        <Avatar src={profileImageUrl ?? undefined} alt={name} sx={avatarSx}>
          {personInitial(person)}
        </Avatar>
      }
      label={name}
      onDelete={onDelete}
      deleteIcon={
        onDelete ? (
          <Box
            component="span"
            id={deleteId}
            // Only a named control claims the role: an unnamed button is worse for assistive
            // tech than the bare icon a caller that opts out already had.
            role={deleteLabel ? 'button' : undefined}
            aria-label={deleteLabel}
            sx={deleteIconSx}
          >
            <AppIcon iconName="XClose" diameter="12px" color="Gray/500" />
          </Box>
        ) : undefined
      }
      sx={badgeSx}
    />
  );
};

const badgeSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 'fit-content',
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  paddingLeft: backrs.spacing['spacing-sm'],
  paddingRight: backrs.spacing['spacing-sm'],
  gap: backrs.spacing['spacing-xs'],
  '& .MuiChip-label': {
    ...typography['text-xs-regular'],
    color: backrs.colors['Gray/700'],
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: 0,
  },
  '& .MuiChip-icon': {
    margin: 0,
  },
  '& .MuiChip-deleteIcon': {
    margin: 0,
  },
});

const avatarSx: SxProps<Theme> = {
  width: '16px',
  height: '16px',
  fontSize: '9px',
};

const deleteIconSx: SxProps<Theme> = {
  display: 'flex',
  cursor: 'pointer',
};
